import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';

const API_BASE = API_BASE_URL;

interface AddOnService {
  id: number;
  name: string;
  description: string;
  price: number;
  pricingType: 'PerPerson' | 'PerGroup';
  categoryId: number;
  categoryName: string;
  isActive: boolean;
}

interface AddOnCategory {
  id: number;
  name: string;
  services: AddOnService[];
}

interface AddonSelectorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (addon: AddOnService) => void;
  excludeIds?: number[]; // IDs of addons already selected
}

const AddonSelectorDialog: React.FC<AddonSelectorDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  excludeIds = []
}) => {
  const [categories, setCategories] = useState<AddOnCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadAddons();
    }
  }, [isOpen]);

  const loadAddons = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/addon/categories?includeInactive=false`);
      if (!response.ok) {
        throw new Error('Failed to load addons');
      }
      const data: AddOnCategory[] = await response.json();
      setCategories(data);
    } catch (err: any) {
      console.error('[AddonSelectorDialog] Error loading addons:', err);
      setError(err.message || 'Fehler beim Laden der Zusatzoptionen');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleSelect = (addon: AddOnService) => {
    onSelect(addon);
    onClose();
  };

  const filteredCategories = categories.map(cat => ({
    ...cat,
    services: cat.services.filter(service =>
      service.isActive &&
      !excludeIds.includes(service.id) &&
      (service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
       service.description?.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  })).filter(cat => cat.services.length > 0);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#1a2332' }}>
            Zusatzoption hinzufügen
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              color: '#666'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '1rem 1.5rem' }}>
          <input
            type="text"
            placeholder="Suche nach Name oder Beschreibung..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '1rem'
            }}
          />
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0 1.5rem 1.5rem'
        }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              Lade Zusatzoptionen...
            </div>
          )}

          {error && (
            <div style={{
              padding: '1rem',
              background: '#fee',
              border: '1px solid #fcc',
              borderRadius: '6px',
              color: '#c00'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && filteredCategories.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: '#666'
            }}>
              Keine Zusatzoptionen gefunden
            </div>
          )}

          {!loading && !error && filteredCategories.map(category => (
            <div key={category.id} style={{ marginBottom: '2rem' }}>
              <h3 style={{
                margin: '0 0 1rem 0',
                fontSize: '1.1rem',
                color: '#1a2332',
                borderBottom: '2px solid #4a9eff',
                paddingBottom: '0.5rem'
              }}>
                {category.name}
              </h3>

              <div style={{
                display: 'grid',
                gap: '1rem',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))'
              }}>
                {category.services.map(service => (
                  <div
                    key={service.id}
                    onClick={() => handleSelect(service)}
                    style={{
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      background: 'white'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#4a9eff';
                      e.currentTarget.style.background = '#f0f8ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.background = 'white';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.5rem'
                    }}>
                      <h4 style={{ margin: 0, color: '#1a2332' }}>
                        {service.name}
                      </h4>
                      <span style={{
                        fontSize: '0.75rem',
                        padding: '0.25rem 0.5rem',
                        background: '#e0f2ff',
                        color: '#0066cc',
                        borderRadius: '4px'
                      }}>
                        {service.pricingType === 'PerPerson' ? 'Pro Person' : 'Pro Gruppe'}
                      </span>
                    </div>

                    {service.description && (
                      <p style={{
                        margin: '0 0 0.75rem 0',
                        color: '#666',
                        fontSize: '0.9rem'
                      }}>
                        {service.description}
                      </p>
                    )}

                    <div style={{
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      color: '#16a34a'
                    }}>
                      {formatCurrency(service.price)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddonSelectorDialog;
